import { useMemo, useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CalendarDays, MapPin, Plane, Camera, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { getTemplateByKey, getVariantByKey } from '@/lib/weddingTemplates';
import {
  buildWeddingV2RenderModel,
  hasRenderablePageBuilderConfig,
  WeddingWebsiteV2Renderer,
} from '@/components/weddingTemplatesV2/WeddingWebsiteV2Renderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RSVP_GROUP_OPTIONS = ['Family - Bride', 'Family - Groom', 'Friends', 'Work Colleagues', 'Other'];

const makeGuest = () => ({
  name: '',
  email: '',
  phone: '',
  group: 'Friends',
  dietary: '',
  additional_guests_count: 0,
});

const V2_THEME_MAP = {
  'stone-ivory': { pageBg: '#f8f6f2', pageText: '#1f1b16', surface: '#ffffff', border: '#ddd5c9', accent: '#5c0120', accentText: '#ffffff' },
  'blush-champagne': { pageBg: '#fcf5f6', pageText: '#3c2430', surface: '#fffdf9', border: '#e8d4da', accent: '#8f2f56', accentText: '#ffffff' },
  'coastal-pearl': { pageBg: '#f2f7fb', pageText: '#1c3347', surface: '#ffffff', border: '#c7d9e6', accent: '#2d5f86', accentText: '#ffffff' },
  'sage-linen': { pageBg: '#f1f6f1', pageText: '#27372a', surface: '#ffffff', border: '#cedacb', accent: '#3f6a4b', accentText: '#ffffff' },
};

const V2_SECTION_ORDER = ['title', 'story', 'wedding_details', 'timeline', 'text_block', 'photo_gallery', 'rsvp', 'photo_upload'];

const formatDate = (dateValue, locale = 'en-GB') => {
  if (!dateValue) return 'Date to be confirmed';
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return dateValue;
  return parsed.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
};

const getSafeErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        if (entry && typeof entry.msg === 'string') return entry.msg;
        return null;
      })
      .filter(Boolean);
    if (parts.length > 0) return parts.join('. ');
  }
  return fallback;
};

export default function WeddingWebsitePublicPage() {
  const { slug } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [website, setWebsite] = useState(null);
  const [rsvpGuest, setRsvpGuest] = useState(makeGuest());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const host = window.location.hostname || '';
    if (!host.endsWith('.emergent.host')) return;
    const pathname = window.location.pathname || '';
    if (!pathname.startsWith('/weddings/')) return;

    const targetUrl = `https://gibraltarweddingexpo.online${pathname}${window.location.search || ''}${window.location.hash || ''}`;
    if (window.location.href !== targetUrl) {
      window.location.replace(targetUrl);
    }
  }, []);

  useEffect(() => {
    const loadWebsite = async () => {
      setLoading(true);
      try {
        try {
          const response = await axios.get(`${API}/weddings/${slug}`);
          setWebsite(response.data);
        } catch {
          const fallbackResponse = await axios.get(`${API}/wedding/${slug}`);
          setWebsite(fallbackResponse.data);
        }
      } catch (error) {
        setWebsite(null);
      } finally {
        setLoading(false);
      }
    };

    loadWebsite();
  }, [slug]);

  const isV2Website = Boolean(website?.mode === 'v2' || (website?.template_id && website?.style_settings));

  const legacyStyle = useMemo(() => {
    const template = getTemplateByKey(website?.template_key);
    const variant = getVariantByKey(template, website?.color_variant_key);
    return { template, variant };
  }, [website]);

  const v2Settings = useMemo(() => {
    if (!isV2Website) return null;
    return website?.style_settings || {};
  }, [isV2Website, website]);

  const v2Theme = useMemo(() => {
    if (!isV2Website) return null;
    return V2_THEME_MAP[v2Settings?.color_theme] || V2_THEME_MAP['stone-ivory'];
  }, [isV2Website, v2Settings]);

  const v2SpacingClass = useMemo(() => {
    if (!isV2Website) return 'space-y-6';
    const map = { compact: 'space-y-3', balanced: 'space-y-4', airy: 'space-y-6', 'airy-plus': 'space-y-8' };
    return map[v2Settings?.spacing_style] || 'space-y-6';
  }, [isV2Website, v2Settings]);

  const v2HeadingFont = useMemo(() => {
    const map = {
      'timeless-seriffed': "'Cormorant Garamond', serif",
      'editorial-grand': "'Playfair Display', serif",
      'refined-contemporary': "'Playfair Display', serif",
      'modern-editorial': "'Manrope', sans-serif",
    };
    return map[v2Settings?.typography_style] || "'Playfair Display', serif";
  }, [v2Settings]);

  const v2BodyFont = useMemo(() => {
    const map = {
      'timeless-seriffed': "'Manrope', sans-serif",
      'editorial-grand': "'Manrope', sans-serif",
      'refined-contemporary': "'Inter', sans-serif",
      'modern-editorial': "'Manrope', sans-serif",
    };
    return map[v2Settings?.typography_style] || "'Manrope', sans-serif";
  }, [v2Settings]);

  const v2TextAlign = useMemo(() => {
    if (!isV2Website) return 'center';
    const value = String(v2Settings?.text_alignment || '').toLowerCase();
    if (value === 'left' || value === 'right' || value === 'center') return value;
    return 'center';
  }, [isV2Website, v2Settings]);

  const v2Content = useMemo(() => {
    if (!isV2Website) return null;
    return {
      couple_names: website?.content?.couple_names || `${website?.partner_one_name || 'Partner One'} & ${website?.partner_two_name || 'Partner Two'}`,
      date: website?.content?.date || website?.wedding_date || '',
      location: website?.content?.location || website?.wedding_location || 'Gibraltar',
      story: website?.content?.story || website?.story_text || '',
      schedule: Array.isArray(website?.content?.schedule) && website.content.schedule.length > 0
        ? website.content.schedule
        : (Array.isArray(website?.schedule_items) ? website.schedule_items : []),
      travel_info: website?.content?.travel_info || website?.travel_info_text || '',
      main_photo: website?.content?.main_photo || website?.hero_image_url || '',
      photos: Array.isArray(website?.content?.photos) ? website.content.photos : [],
      story_images: Array.isArray(website?.content?.story_images) ? website.content.story_images : [],
      photo_sharing_message: website?.content?.photo_sharing_message || website?.photo_cta_text || '',
    };
  }, [isV2Website, website]);

  const v2SectionConfig = useMemo(() => {
    if (!isV2Website) return null;
    const raw = v2Settings?.section_config || {};
    return {
      hero: {
        visible: true,
        title: 'Wedding Website',
        text: '',
        image_url: v2Content?.main_photo || '',
        image_orientation: 'landscape',
        layout: 'centered',
        ...(raw.hero || {}),
      },
      story: {
        visible: true,
        title: 'Our Story',
        text: v2Content?.story || '',
        image_url: (Array.isArray(v2Content?.story_images) && v2Content.story_images[0]) || '',
        image_orientation: 'landscape',
        layout: 'standard',
        ...(raw.story || {}),
      },
      details: {
        visible: true,
        title: 'Wedding Details',
        text: '',
        image_url: '',
        image_orientation: 'landscape',
        layout: 'standard',
        ...(raw.details || {}),
      },
      schedule: {
        visible: true,
        title: 'Schedule',
        text: (Array.isArray(v2Content?.schedule) ? v2Content.schedule.map((item) => item?.title || '').filter(Boolean).join('\n') : ''),
        image_url: '',
        image_orientation: 'landscape',
        layout: 'standard',
        ...(raw.schedule || {}),
      },
      travel: {
        visible: true,
        title: 'Travel & Info',
        text: v2Content?.travel_info || '',
        image_url: '',
        image_orientation: 'landscape',
        layout: 'standard',
        ...(raw.travel || {}),
      },
    };
  }, [isV2Website, v2Content, v2Settings]);

  const v2RenderModel = useMemo(
    () => buildWeddingV2RenderModel({
      sectionConfig: v2Settings?.section_config,
      designSettings: v2Settings,
      content: v2Content,
      rsvpEnabled: website?.rsvp_enabled !== false,
    }),
    [v2Content, v2Settings, website],
  );

  const hasPageBuilderConfig = useMemo(
    () => hasRenderablePageBuilderConfig(v2Settings?.section_config),
    [v2Settings],
  );

  const pageTitle = useMemo(() => {
    if (!website) return 'Wedding website';
    const coupleNames = isV2Website
      ? (v2Content?.couple_names || 'Wedding Couple')
      : `${website.partner_one_name || 'Partner One'} & ${website.partner_two_name || 'Partner Two'}`;
    return `${coupleNames} Wedding in Gibraltar`;
  }, [isV2Website, v2Content, website]);

  const pageDescription = useMemo(() => {
    if (!website) return 'Wedding website in Gibraltar';
    const coupleNames = isV2Website
      ? (v2Content?.couple_names || 'Wedding Couple')
      : `${website.partner_one_name || 'Partner One'} & ${website.partner_two_name || 'Partner Two'}`;
    return `Join ${coupleNames} for their wedding celebration in Gibraltar. View details, schedule, and RSVP.`;
  }, [isV2Website, v2Content, website]);

  const canonicalUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/weddings/${slug}`;
  }, [slug]);

  useEffect(() => {
    if (!website) {
      document.title = 'Wedding website not found';
      return;
    }

    document.title = pageTitle;
    const upsertMeta = (key, content, attr = 'name') => {
      let meta = document.querySelector(`meta[${attr}="${key}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, key);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const upsertCanonical = (href) => {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    const jsonLdPayload = {
      '@context': 'https://schema.org',
      '@type': 'WeddingEvent',
      name: pageTitle,
      description: pageDescription,
      startDate: isV2Website ? (v2Content?.date || '') : (website?.wedding_date || ''),
      location: {
        '@type': 'Place',
        name: isV2Website ? (v2Content?.location || 'Gibraltar') : (website?.wedding_location || 'Gibraltar'),
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Gibraltar',
        },
      },
    };

    let jsonLdNode = document.querySelector('script[data-testid="wedding-public-jsonld"]');
    if (!jsonLdNode) {
      jsonLdNode = document.createElement('script');
      jsonLdNode.setAttribute('type', 'application/ld+json');
      jsonLdNode.setAttribute('data-testid', 'wedding-public-jsonld');
      document.head.appendChild(jsonLdNode);
    }
    jsonLdNode.textContent = JSON.stringify(jsonLdPayload);

    upsertMeta('description', pageDescription);
    upsertMeta('robots', 'index,follow');
    upsertMeta('og:title', pageTitle, 'property');
    upsertMeta('og:description', pageDescription, 'property');
    upsertMeta('og:type', 'website', 'property');
    upsertMeta('og:url', canonicalUrl, 'property');
    if (isV2Website && v2Content?.main_photo) {
      upsertMeta('og:image', v2Content.main_photo, 'property');
    }
    if (canonicalUrl) {
      upsertCanonical(canonicalUrl);
    }
  }, [canonicalUrl, isV2Website, pageDescription, pageTitle, v2Content, website, location.pathname]);

  const submitRsvp = async (event) => {
    event.preventDefault();

    if (!rsvpGuest.name.trim()) {
      toast.error('Please add your name.');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/wedding/${slug}/rsvp`, {
        name: rsvpGuest.name.trim(),
        email: rsvpGuest.email.trim(),
        phone: rsvpGuest.phone.trim(),
        group: rsvpGuest.group,
        dietary: rsvpGuest.dietary.trim(),
        additional_guests_count: Number(rsvpGuest.additional_guests_count || 0),
      });
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('planner_guest_refresh_signal', String(Date.now()));
      }
      toast.success('Thanks! Your RSVP has been received.');
      setRsvpGuest(makeGuest());
    } catch (error) {
      toast.error(getSafeErrorMessage(error, 'Unable to submit RSVP right now.'));
    } finally {
      setSaving(false);
    }
  };

  const renderRsvpForm = (surfaceClassName, buttonClassName, cardStyle = null) => (
    <section className="px-6 py-10" data-testid="wedding-public-rsvp">
      <form onSubmit={submitRsvp} className={`mx-auto max-w-3xl rounded-2xl border p-6 space-y-4 ${surfaceClassName}`} data-testid="wedding-public-rsvp-form" style={cardStyle || undefined}>
        <h2 className="text-2xl font-semibold">RSVP</h2>
        <p className="text-sm">Please let us know if you can join us.</p>
        <div className="rounded-xl border border-white/40 bg-white/60 p-4 space-y-4" data-testid="wedding-public-rsvp-guest-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="rsvp-name-input" data-testid="wedding-public-rsvp-name-label">Name *</Label>
              <Input
                id="rsvp-name-input"
                value={rsvpGuest.name}
                onChange={(e) => setRsvpGuest((prev) => ({ ...prev, name: e.target.value }))}
                required
                placeholder="Guest name"
                data-testid="wedding-public-rsvp-name-input"
              />
            </div>
            <div>
              <Label htmlFor="rsvp-email-input" data-testid="wedding-public-rsvp-email-label">Email</Label>
              <Input
                id="rsvp-email-input"
                type="email"
                value={rsvpGuest.email}
                onChange={(e) => setRsvpGuest((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="guest@email.com"
                data-testid="wedding-public-rsvp-email-input"
              />
            </div>
            <div>
              <Label htmlFor="rsvp-phone-input" data-testid="wedding-public-rsvp-phone-label">Phone</Label>
              <Input
                id="rsvp-phone-input"
                value={rsvpGuest.phone}
                onChange={(e) => setRsvpGuest((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="Phone number"
                data-testid="wedding-public-rsvp-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="rsvp-group-select" data-testid="wedding-public-rsvp-group-label">Group</Label>
              <select
                id="rsvp-group-select"
                value={rsvpGuest.group}
                onChange={(e) => setRsvpGuest((prev) => ({ ...prev, group: e.target.value }))}
                className="w-full rounded-md border border-stone-300 bg-white p-2"
                data-testid="wedding-public-rsvp-group-select"
              >
                {RSVP_GROUP_OPTIONS.map((groupOption) => (
                  <option key={groupOption} value={groupOption}>{groupOption}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="rsvp-dietary-input" data-testid="wedding-public-rsvp-dietary-label">Dietary Requirements</Label>
              <Input
                id="rsvp-dietary-input"
                value={rsvpGuest.dietary}
                onChange={(e) => setRsvpGuest((prev) => ({ ...prev, dietary: e.target.value }))}
                placeholder="e.g., Vegetarian, Gluten-free"
                data-testid="wedding-public-rsvp-dietary-input"
              />
            </div>
            <div>
              <Label htmlFor="rsvp-additional-count-input" data-testid="wedding-public-rsvp-additional-count-label">Additional Guests Count</Label>
              <Input
                id="rsvp-additional-count-input"
                type="number"
                min={0}
                value={rsvpGuest.additional_guests_count}
                onChange={(e) => setRsvpGuest((prev) => ({ ...prev, additional_guests_count: Number(e.target.value || 0) }))}
                data-testid="wedding-public-rsvp-additional-count-input"
              />
            </div>
          </div>
        </div>

        <Button type="submit" className={`w-full ${buttonClassName}`} disabled={saving} data-testid="wedding-public-rsvp-submit-button">
          {saving ? 'Submitting...' : 'Submit RSVP'}
        </Button>
      </form>
    </section>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="wedding-public-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#5C0120]" />
      </div>
    );
  }

  if (!website) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4" data-testid="wedding-public-not-found">
        <div className="max-w-lg rounded-xl border border-stone-200 bg-white p-8 text-center">
          <h1 className="text-3xl font-semibold text-stone-900 mb-2">Wedding website not found</h1>
          <p className="text-stone-600">This page may still be in draft mode or the link is incorrect.</p>
        </div>
      </div>
    );
  }

  if (isV2Website && v2Theme && v2Content) {
    const uppercaseHeadings = Boolean(v2Settings?.uppercase_headings);
    const selectedSections = Array.isArray(v2Settings?.sections) && v2Settings.sections.length > 0 ? v2Settings.sections : V2_SECTION_ORDER;
    const sections = V2_SECTION_ORDER.filter((section) => selectedSections.includes(section));
    const headingClass = uppercaseHeadings ? 'uppercase tracking-[0.12em]' : '';

    const renderMediaLayout = (url, orientation, layout, index = 0) => {
      if (!url) return null;
      const descriptiveAlt = `${v2Content.couple_names || 'Wedding couple'} wedding Gibraltar`;
      if (orientation === 'portrait') {
        const imageOnLeft = layout === 'image-left' || (layout !== 'image-right' && index % 2 === 0);
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {imageOnLeft && (
              <div className="overflow-hidden border" style={{ borderColor: v2Theme.border }}>
                <img src={url} alt={descriptiveAlt} className="w-full h-full aspect-[4/5] object-cover object-center" />
              </div>
            )}
            {!imageOnLeft && <div className="hidden md:block" />}
            {!imageOnLeft && (
              <div className="overflow-hidden border" style={{ borderColor: v2Theme.border }}>
                <img src={url} alt={descriptiveAlt} className="w-full h-full aspect-[4/5] object-cover object-center" />
              </div>
            )}
          </div>
        );
      }

      return (
        <div className="w-full overflow-hidden border mt-4" style={{ borderColor: v2Theme.border }}>
          <img src={url} alt={descriptiveAlt} className="w-full h-full aspect-[16/9] object-cover object-center" />
        </div>
      );
    };

    if (hasPageBuilderConfig) {
      return (
        <WeddingWebsiteV2Renderer
          renderModel={v2RenderModel}
          renderRsvpSection={({ theme }) => renderRsvpForm('', '', { borderColor: theme.border, backgroundColor: theme.surface })}
          testIdPrefix="wedding-public"
        />
      );
    }

    const sectionTypeToConfigKey = {
      title: 'hero',
      story: 'story',
      wedding_details: 'details',
      timeline: 'schedule',
      text_block: 'travel',
    };

    const renderSectionImage = (sectionType, sectionIndex) => {
      const configKey = sectionTypeToConfigKey[sectionType];
      const section = configKey ? v2SectionConfig?.[configKey] : null;
      if (!section?.image_url) return null;

      const descriptiveAlt = `${v2Content.couple_names || 'Wedding couple'} wedding Gibraltar`;
      if (section.image_orientation === 'portrait') {
        const imageOnLeft = section.layout === 'image-left' || (section.layout !== 'image-right' && sectionIndex % 2 === 0);
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid={`wedding-public-${sectionType}-portrait-wrap`}>
            {imageOnLeft && (
              <div className="overflow-hidden border" style={{ borderColor: v2Theme.border }}>
                <img src={section.image_url} alt={descriptiveAlt} className="w-full h-full aspect-[4/5] object-cover object-center" />
              </div>
            )}
            {!imageOnLeft && <div className="hidden md:block" />}
            {!imageOnLeft && (
              <div className="overflow-hidden border" style={{ borderColor: v2Theme.border }}>
                <img src={section.image_url} alt={descriptiveAlt} className="w-full h-full aspect-[4/5] object-cover object-center" />
              </div>
            )}
          </div>
        );
      }

      return (
        <div className="w-full overflow-hidden border mt-4" style={{ borderColor: v2Theme.border }} data-testid={`wedding-public-${sectionType}-landscape-wrap`}>
          <img src={section.image_url} alt={descriptiveAlt} className="w-full h-full aspect-[16/9] object-cover object-center" />
        </div>
      );
    };

    return (
      <div className="min-h-screen" style={{ backgroundColor: v2Theme.pageBg, color: v2Theme.pageText, fontFamily: v2BodyFont }} data-testid="wedding-public-page-v2">
        <header
          className={`px-6 py-14 border-b ${v2SpacingClass}`}
          style={{
            borderColor: v2Theme.border,
            backgroundImage: v2Content.main_photo ? `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url('${v2Content.main_photo}')` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: v2Content.main_photo ? '#ffffff' : v2Theme.pageText,
            textAlign: v2TextAlign,
          }}
          data-testid="wedding-public-v2-hero"
        >
          <p className="text-xs uppercase tracking-[0.2em] mb-2">{v2SectionConfig?.hero?.title || 'Wedding Website'}</p>
          <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-semibold ${headingClass}`} style={{ fontFamily: v2HeadingFont }} data-testid="wedding-public-couple-names">{v2Content.couple_names}</h1>
          {(v2Content.date || v2Content.location) && (
            <p className="mt-3 text-sm sm:text-base" data-testid="wedding-public-date-location">{formatDate(v2Content.date, 'en-GB')} {v2Content.location ? `· ${v2Content.location}` : ''}</p>
          )}
          {v2SectionConfig?.hero?.text && <p className="mt-4 max-w-2xl mx-auto">{v2SectionConfig.hero.text}</p>}
        </header>

          <main className={`max-w-4xl mx-auto px-6 py-8 ${v2SpacingClass}`} style={{ textAlign: v2TextAlign }} data-testid="wedding-public-v2-sections-wrap">
          {sections.includes('story') && v2SectionConfig?.story?.visible !== false && (
            <article className="border p-6" style={{ borderColor: v2Theme.border, backgroundColor: v2Theme.surface }} data-testid="wedding-public-story">
              <h2 className={`text-2xl font-semibold mb-3 ${headingClass}`} style={{ fontFamily: v2HeadingFont, color: v2Theme.accent }}>{v2SectionConfig.story.title || 'Our Story'}</h2>
              {(v2SectionConfig.story.text || v2Content.story) && <p className="whitespace-pre-line leading-relaxed">{v2SectionConfig.story.text || v2Content.story}</p>}
              {renderSectionImage('story', 1)}
            </article>
          )}

          {sections.includes('wedding_details') && v2SectionConfig?.details?.visible !== false && (
            <article className="border p-6" style={{ borderColor: v2Theme.border, backgroundColor: v2Theme.surface }} data-testid="wedding-public-details">
              <h2 className={`text-2xl font-semibold mb-3 ${headingClass}`} style={{ fontFamily: v2HeadingFont, color: v2Theme.accent }}>{v2SectionConfig.details.title || 'Wedding Details'}</h2>
              {v2SectionConfig.details.text && <p className="whitespace-pre-line leading-relaxed mb-4">{v2SectionConfig.details.text}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="border p-3" style={{ borderColor: v2Theme.border }}>
                  <p className="text-xs uppercase tracking-[0.16em] mb-1">Ceremony</p>
                  {v2Content.date && <p className="text-sm flex items-center gap-2"><CalendarDays className="h-4 w-4" />{formatDate(v2Content.date, 'en-GB')}</p>}
                  {v2Content.location && <p className="text-sm flex items-center gap-2"><MapPin className="h-4 w-4" />{v2Content.location}</p>}
                </div>
                <div className="border p-3" style={{ borderColor: v2Theme.border }}>
                  <p className="text-xs uppercase tracking-[0.16em] mb-1">Reception</p>
                  {v2Content.date && <p className="text-sm flex items-center gap-2"><CalendarDays className="h-4 w-4" />{formatDate(v2Content.date, 'en-GB')}</p>}
                  {v2Content.location && <p className="text-sm flex items-center gap-2"><MapPin className="h-4 w-4" />{v2Content.location}</p>}
                </div>
              </div>
              {renderSectionImage('wedding_details', 2)}
            </article>
          )}

          {sections.includes('timeline') && v2SectionConfig?.schedule?.visible !== false && (
            <article className="border p-6" style={{ borderColor: v2Theme.border, backgroundColor: v2Theme.surface }} data-testid="wedding-public-schedule">
              <h2 className={`text-2xl font-semibold mb-4 ${headingClass}`} style={{ fontFamily: v2HeadingFont, color: v2Theme.accent }}>{v2SectionConfig.schedule.title || 'Schedule'}</h2>
              {v2SectionConfig.schedule.text ? (
                <div className="space-y-3">
                  {v2SectionConfig.schedule.text.split('\n').map((line, index) => line.trim()).filter(Boolean).map((line, index) => (
                    <div key={`v2-schedule-text-${index}`} className="border p-3" style={{ borderColor: v2Theme.border }} data-testid={`wedding-public-schedule-item-${index}`}>
                      <p className="font-semibold">{line}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {v2Content.schedule.map((item, index) => (
                    <div key={`v2-schedule-${index}`} className="border p-3" style={{ borderColor: v2Theme.border }} data-testid={`wedding-public-schedule-item-${index}`}>
                      <p className="font-semibold">{item.time || 'TBC'} · {item.title || 'Event'}</p>
                      {item.notes && <p className="text-sm mt-1">{item.notes}</p>}
                    </div>
                  ))}
                </div>
              )}
              {renderSectionImage('timeline', 3)}
            </article>
          )}

          {sections.includes('text_block') && v2SectionConfig?.travel?.visible !== false && (v2SectionConfig.travel.text || v2Content.travel_info) && (
            <article className="border p-6" style={{ borderColor: v2Theme.border, backgroundColor: v2Theme.surface }} data-testid="wedding-public-travel">
              <h2 className={`text-2xl font-semibold mb-3 flex items-center gap-2 ${headingClass}`} style={{ fontFamily: v2HeadingFont, color: v2Theme.accent }}><Plane className="h-5 w-5" />{v2SectionConfig.travel.title || 'Travel & Info'}</h2>
              <p className="whitespace-pre-line leading-relaxed">{v2SectionConfig.travel.text || v2Content.travel_info}</p>
              {renderSectionImage('text_block', 4)}
            </article>
          )}

          {sections.includes('photo_gallery') && (v2Content.photos || []).length > 0 && (
            <section className="border p-6" style={{ borderColor: v2Theme.border, backgroundColor: v2Theme.surface }} data-testid="wedding-public-photo-gallery">
              <h2 className={`text-2xl font-semibold mb-3 ${headingClass}`} style={{ fontFamily: v2HeadingFont, color: v2Theme.accent }}>Photos</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="wedding-public-photo-gallery-grid">
                {v2Content.photos.map((photo, index) => (
                  <img key={`gallery-${index}`} src={photo} alt={`Wedding photo ${index + 1}`} className="w-full aspect-square object-cover" data-testid={`wedding-public-photo-gallery-item-${index}`} />
                ))}
              </div>
            </section>
          )}

          {website.rsvp_enabled !== false && sections.includes('rsvp') && renderRsvpForm('', '', { borderColor: v2Theme.border, backgroundColor: v2Theme.surface })}

          {sections.includes('photo_upload') && v2Content.photo_sharing_message && (
            <section className="border p-6 text-center" style={{ borderColor: v2Theme.border, backgroundColor: v2Theme.surface }} data-testid="wedding-public-photo-cta">
              <h2 className={`text-2xl font-semibold mb-2 flex items-center justify-center gap-2 ${headingClass}`} style={{ fontFamily: v2HeadingFont, color: v2Theme.accent }}><Camera className="h-5 w-5" />Photo Upload</h2>
              <p className="mb-4">{v2Content.photo_sharing_message}</p>
              <Button type="button" style={{ backgroundColor: v2Theme.accent, color: v2Theme.accentText }} data-testid="wedding-public-photo-cta-button">Upload Photos (Coming Soon)</Button>
            </section>
          )}
        </main>

        <footer className="px-6 py-10 text-center text-sm" data-testid="wedding-public-footer">
          <p className="flex items-center justify-center gap-2"><Heart className="h-4 w-4" />Powered by Gibraltar Wedding Expo</p>
        </footer>
      </div>
    );
  }

  const names = `${website.partner_one_name || 'Partner One'} & ${website.partner_two_name || 'Partner Two'}`;
  return (
    <div className={`min-h-screen ${legacyStyle.variant.page} ${legacyStyle.template.fontClass}`} data-testid="wedding-public-page">
      <section
        className={`relative px-6 py-16 md:py-24 ${legacyStyle.template.heroLayout === 'split' ? 'md:grid md:grid-cols-2 md:gap-8 md:items-center' : ''}`}
        style={website.hero_image_url ? { backgroundImage: legacyStyle.template.heroLayout === 'full-image' ? `linear-gradient(rgba(0,0,0,0.35),rgba(0,0,0,0.35)),url(${website.hero_image_url})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
        data-testid="wedding-public-hero"
      >
        <div className={legacyStyle.template.heroLayout === 'full-image' ? 'max-w-3xl mx-auto text-center text-white' : 'max-w-2xl mx-auto text-center md:text-left'}>
          <p className="uppercase tracking-[0.2em] text-xs mb-3" data-testid="wedding-public-hero-overline">We are getting married</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold" data-testid="wedding-public-couple-names">{names}</h1>
          <p className="mt-4 text-lg" data-testid="wedding-public-date-location">{formatDate(website.wedding_date)} · {website.wedding_location || 'Gibraltar'}</p>
        </div>
      </section>

      <section className="px-6 py-12">
        <div className={`mx-auto max-w-3xl rounded-2xl border p-6 md:p-8 ${legacyStyle.variant.surface}`} data-testid="wedding-public-story">
          <h2 className="text-2xl font-semibold mb-3">Our Story</h2>
          <p className="leading-relaxed whitespace-pre-line">{website.story_text || 'Our story will be shared here soon.'}</p>
        </div>
      </section>

      {renderRsvpForm(legacyStyle.variant.surface, legacyStyle.variant.button)}

      <footer className="px-6 py-10 text-center text-sm" data-testid="wedding-public-footer">
        <p className="flex items-center justify-center gap-2"><Heart className="h-4 w-4" />Powered by Gibraltar Wedding Expo</p>
      </footer>
    </div>
  );
}
